/* 외부 API 키 관리 (SFL 통합 키 저장소와 연동)
   - 코드(키)만 입력하면 즉시 .107 SFL DB(integration_config)에 저장됨
   - .107 SFL은 DB-first → env 폴백 → default 순으로 읽으므로 입력 즉시 활성화
   - SFL 관리자 토큰을 1회 입력하면 브라우저 localStorage에 보관(만료 시 재입력)
*/
import React, { useCallback, useEffect, useState } from "react";
import { fetchTiaExternalControl, updateTiaExternalControl } from "../api/adminApi";

const SFL_API_BASE = "https://ebe.kr/api/v1";
const SFL_TOKEN_KEY = "sfl_admin_token";

function maskKeyish(v) {
  if (!v) return "";
  if (v.length <= 4) return "••••";
  return v.slice(0, 2) + "••••" + v.slice(-2);
}

function ExternalApiPanel({ apiBase, token }) {
  const [sflToken, setSflToken] = useState(() => localStorage.getItem(SFL_TOKEN_KEY) || "");
  const [tokenInput, setTokenInput] = useState("");
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});            // {SMS_API_KEY: "new value", ...}
  const [tiaControl, setTiaControl] = useState(null);
  const [tiaDraft, setTiaDraft] = useState({ external_calls_enabled: false, daily_call_limit: 0, monthly_call_limit: 0, engine_pref: "local" });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const load = useCallback(async () => {
    if (!sflToken) return;
    setBusy(true); setErrorMsg("");
    try {
      const res = await fetch(`${SFL_API_BASE}/admin/integrations?token=${encodeURIComponent(sflToken)}`);
      if (res.status === 401 || res.status === 403) {
        setErrorMsg("SFL 관리자 토큰이 만료되었거나 권한이 부족합니다. 다시 입력하세요.");
        return;
      }
      if (!res.ok) { setErrorMsg("불러오기 실패: HTTP " + res.status); return; }
      const data = await res.json();
      setItems(data.items || []);
    } catch (e) { setErrorMsg("연결 실패: " + (e && e.message ? e.message : String(e))); }
    finally { setBusy(false); }
  }, [sflToken]);

  useEffect(() => { load(); }, [load]);

  const loadTiaControl = useCallback(async () => {
    if (!apiBase || !token) return;
    try {
      const data = await fetchTiaExternalControl(apiBase, token);
      setTiaControl(data);
      setTiaDraft(data.settings || {});
    } catch (e) {
      setErrorMsg((e && e.message) || String(e));
    }
  }, [apiBase, token]);

  useEffect(() => { loadTiaControl(); }, [loadTiaControl]);

  const saveTiaControl = async () => {
    setBusy(true); setErrorMsg(""); setNotice("");
    try {
      const data = await updateTiaExternalControl(apiBase, token, {
        external_calls_enabled: !!tiaDraft.external_calls_enabled,
        daily_call_limit: Number(tiaDraft.daily_call_limit || 0),
        monthly_call_limit: Number(tiaDraft.monthly_call_limit || 0),
        engine_pref: tiaDraft.engine_pref || "local",
      });
      setTiaControl(data);
      setTiaDraft(data.settings || {});
      setNotice("TIA 외부 호출 제어 설정을 저장했습니다.");
    } catch (e) {
      setErrorMsg((e && e.message) || String(e));
    } finally {
      setBusy(false);
    }
  };

  const saveToken = () => {
    const t = (tokenInput || "").trim();
    if (!t) return;
    localStorage.setItem(SFL_TOKEN_KEY, t);
    setSflToken(t); setTokenInput(""); setNotice("토큰 저장됨. SFL 연동 키를 불러옵니다.");
  };
  const clearToken = () => {
    localStorage.removeItem(SFL_TOKEN_KEY); setSflToken(""); setItems([]); setNotice("");
  };

  const draftFor = (key) => (drafts[key] !== undefined ? drafts[key] : "");
  const setDraft = (key, value) => setDrafts((p) => ({ ...p, [key]: value }));

  const saveOne = async (key) => {
    const value = draftFor(key);
    if (!value) { setErrorMsg("값을 입력하세요."); return; }
    if (value.indexOf("•") >= 0) { setErrorMsg("마스킹 표시값(••••)은 저장할 수 없습니다. 실제 키를 입력하세요."); return; }
    setBusy(true); setErrorMsg(""); setNotice("");
    try {
      const res = await fetch(`${SFL_API_BASE}/admin/integrations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: sflToken, key, value }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setErrorMsg("저장 실패: " + (j.error || "HTTP " + res.status));
        return;
      }
      setDrafts((p) => { const n = { ...p }; delete n[key]; return n; });
      setNotice(`${key} 저장됨 — 즉시 활용됩니다.`);
      await load();
    } catch (e) { setErrorMsg("저장 중 오류: " + (e && e.message ? e.message : String(e))); }
    finally { setBusy(false); }
  };

  const deleteOne = async (key) => {
    if (!window.confirm(`${key} 키를 삭제할까요? (env 기본값이 있으면 그 값으로 폴백)`)) return;
    setBusy(true); setErrorMsg(""); setNotice("");
    try {
      const res = await fetch(`${SFL_API_BASE}/admin/integrations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: sflToken, key, value: "" }),
      });
      if (!res.ok) { setErrorMsg("삭제 실패: HTTP " + res.status); return; }
      setNotice(`${key} 삭제됨.`); await load();
    } finally { setBusy(false); }
  };

  const testOne = async (kind) => {
    setBusy(true); setErrorMsg(""); setNotice("연결 테스트 중…");
    try {
      const res = await fetch(`${SFL_API_BASE}/admin/integrations/test?kind=${encodeURIComponent(kind)}&token=${encodeURIComponent(sflToken)}`);
      if (!res.ok) { setErrorMsg("테스트 실패: HTTP " + res.status); setNotice(""); return; }
      const d = await res.json();
      if (d.configured === false) { setNotice(`${kind}: 키가 설정되지 않았습니다.`); return; }
      if (!d.ok) { setNotice(""); setErrorMsg(`${kind} 연결 실패: ${d.message || d.error}`); return; }
      const detail =
        kind === "tobe_ai" ? `주체 ${d.sub || "—"} / 역할 ${d.role || "—"}` :
        d.parsed_water_temp != null ? `수온 ${d.parsed_water_temp}°C` : "응답 수신 OK";
      setNotice(`✅ ${kind} 연결됨 — ${detail}`);
    } catch (e) { setErrorMsg("테스트 중 오류: " + (e && e.message ? e.message : String(e))); }
    finally { setBusy(false); }
  };

  if (!sflToken) {
    return (
      <section className="admin-section">
        <TiaExternalControlCard
          control={tiaControl}
          draft={tiaDraft}
          setDraft={setTiaDraft}
          onSave={saveTiaControl}
          busy={busy}
        />
        <header className="admin-section-header">
          <h2>외부 API 키 관리</h2>
          <p>KHOA·KMA·PG·TOBE_AI 등 외부 연동 키를 한 곳에서 입력·테스트합니다. 입력 즉시 SFL(ebe.kr)에서 활용됩니다.</p>
        </header>
        <div className="admin-card" style={{ padding: 18, maxWidth: 560 }}>
          <p style={{ marginTop: 0 }}>
            <strong>SFL 관리자 토큰</strong>을 한 번 입력하세요. (만료 시 재입력) <br />
            <small style={{ color: "#666" }}>
              발급 방법: <a href="https://ebe.kr/console.html" target="_blank" rel="noreferrer">ebe.kr/console.html</a> 로그인 → 브라우저 콘솔에서{" "}
              <code>localStorage.getItem('eobok_token')</code> 출력값 복사
            </small>
          </p>
          <input
            type="password" autoComplete="off" placeholder="SFL 관리자 토큰 (tok-…)"
            value={tokenInput} onChange={(e) => setTokenInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") saveToken(); }}
            style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #ccc", marginTop: 8 }}
          />
          <button className="admin-approve-button" onClick={saveToken} style={{ marginTop: 10 }}>토큰 저장 후 키 관리 시작</button>
        </div>
      </section>
    );
  }

  return (
    <section className="admin-section">
      <TiaExternalControlCard
        control={tiaControl}
        draft={tiaDraft}
        setDraft={setTiaDraft}
        onSave={saveTiaControl}
        busy={busy}
      />
      <header className="admin-section-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div>
          <h2>외부 API 키 관리</h2>
          <p>입력 즉시 SFL(ebe.kr)에서 활용됩니다. 비밀값은 마스킹되어 표시됩니다.</p>
        </div>
        <button onClick={clearToken} style={{ background: "transparent", border: "1px solid #ddd", padding: "6px 12px", borderRadius: 6 }}>
          SFL 토큰 해제
        </button>
      </header>
      {notice && <div className="admin-inline-notice" role="status"><span>{notice}</span></div>}
      {errorMsg && <div className="admin-error">{errorMsg}</div>}
      {busy && !items.length && <div>불러오는 중…</div>}

      <div style={{ display: "grid", gap: 14, maxWidth: 760 }}>
        {items.map((it) => {
          const live = it.status_kind === "live";
          const testable = ["khoa", "kma", "tobe_ai"].indexOf(it.id) >= 0;
          return (
            <div key={it.id} className="admin-card" style={{ padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <strong style={{ fontSize: 15 }}>{it.label}</strong>
                <span style={{ fontSize: 11, color: live ? "#0a8" : "#b8841d" }}>
                  {live ? "✅ 적용중" : "🔧 연동대기"} · {it.configured ? "키 입력됨" : "미입력"}
                </span>
              </div>
              <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>{it.desc}</div>
              <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>📋 발급처: {it.doc}</div>

              {it.fields.map((f) => (
                <div key={f.key} style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, color: "#444" }}>
                    {f.label} {f.secret ? "🔒" : ""} {f.source ? <span style={{ color: "#999" }}>[{f.source}]</span> : null}
                  </div>
                  <input
                    type={f.secret ? "password" : "text"}
                    autoComplete="off" spellCheck={false}
                    placeholder={f.set ? (f.secret ? `현재 ${f.value_masked} · 변경 시에만 입력` : "") : (f.placeholder || "")}
                    value={draftFor(f.key)}
                    onChange={(e) => setDraft(f.key, e.target.value)}
                    style={{ width: "100%", padding: 9, borderRadius: 6, border: "1px solid #ccc", marginTop: 4 }}
                  />
                  {f.set && (
                    <button
                      onClick={() => deleteOne(f.key)}
                      style={{ marginTop: 6, background: "transparent", border: "none", color: "#c44", fontSize: 11, cursor: "pointer", padding: 0 }}
                    >
                      이 키 삭제
                    </button>
                  )}
                </div>
              ))}

              <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                {it.fields.map((f) => (
                  draftFor(f.key) ? (
                    <button key={f.key} className="admin-approve-button" onClick={() => saveOne(f.key)} disabled={busy}>
                      {f.label} 저장
                    </button>
                  ) : null
                ))}
                {testable && (
                  <button onClick={() => testOne(it.id)} disabled={busy}
                    style={{ background: "transparent", border: "1px solid #ddd", padding: "6px 12px", borderRadius: 6 }}>
                    연결 테스트
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function TiaExternalControlCard({ control, draft, setDraft, onSave, busy }) {
  const usage = control?.usage || [];
  return (
    <div className="admin-card" style={{ padding: 16, maxWidth: 860, marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div>
          <strong style={{ fontSize: 15 }}>TIA 외부 AI 호출 제어</strong>
          <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
            기본은 차단입니다. 명시적으로 허용해야 Gemini/OpenAI/Anthropic 호출이 가능하고, 한도 0은 무제한입니다.
          </div>
        </div>
        <span style={{ fontSize: 12, color: draft.external_calls_enabled ? "#0a8" : "#c44" }}>
          {draft.external_calls_enabled ? "외부 호출 허용" : "외부 호출 차단"}
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginTop: 14 }}>
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
          <input
            type="checkbox"
            checked={!!draft.external_calls_enabled}
            onChange={(e) => setDraft((p) => ({ ...p, external_calls_enabled: e.target.checked }))}
          />
          외부 호출 허용
        </label>
        <label style={{ fontSize: 12 }}>
          기본 엔진
          <select
            value={draft.engine_pref || "local"}
            onChange={(e) => setDraft((p) => ({ ...p, engine_pref: e.target.value }))}
            style={{ width: "100%", marginTop: 4, padding: 8, borderRadius: 6, border: "1px solid #ccc" }}
          >
            <option value="local">local</option>
            <option value="gemini">gemini</option>
            <option value="openai">openai</option>
            <option value="anthropic">anthropic</option>
          </select>
        </label>
        <label style={{ fontSize: 12 }}>
          일일 호출 제한
          <input
            type="number" min="0"
            value={draft.daily_call_limit ?? 0}
            onChange={(e) => setDraft((p) => ({ ...p, daily_call_limit: e.target.value }))}
            style={{ width: "100%", marginTop: 4, padding: 8, borderRadius: 6, border: "1px solid #ccc" }}
          />
        </label>
        <label style={{ fontSize: 12 }}>
          월간 호출 제한
          <input
            type="number" min="0"
            value={draft.monthly_call_limit ?? 0}
            onChange={(e) => setDraft((p) => ({ ...p, monthly_call_limit: e.target.value }))}
            style={{ width: "100%", marginTop: 4, padding: 8, borderRadius: 6, border: "1px solid #ccc" }}
          />
        </label>
      </div>
      <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button className="admin-approve-button" onClick={onSave} disabled={busy}>TIA 설정 저장</button>
        <small style={{ color: "#777" }}>{control?.policy || "fail-closed 정책"}</small>
      </div>
      {usage.length > 0 && (
        <div style={{ marginTop: 12, fontSize: 12 }}>
          <strong>최근 호출량</strong>
          <div style={{ display: "grid", gap: 6, marginTop: 6 }}>
            {usage.slice(0, 8).map((row) => (
              <div key={`${row.period}-${row.provider}-${row.model}`} style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #eee", paddingTop: 6 }}>
                <span>{row.period} · {row.provider} · {row.model}</span>
                <span>{row.calls}회</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ExternalApiPanel;
